import { useRef, useCallback, useEffect } from 'react';
import type { GeneralFunction } from '@asherng/common-types';
import type { UseDebouncedCallback, SetTimeOutType } from './types';

const useDebouncedCallback: UseDebouncedCallback = (callback, delay, options) => {
  const maxWaitHandler = useRef<SetTimeOutType>(undefined);

  const leading = options?.leading;

  const trailing = options?.trailing ?? true;

  const maxWait = options?.maxWait;

  const maxWaitArgs = useRef<any[]>([]);

  const leadingCall = useRef(false);

  const functionTimeoutHandler = useRef<SetTimeOutType>(undefined);

  const isComponentUnmounted = useRef(false);

  const debouncedFunction = useRef(callback);

  const cancelDebouncedCallback: GeneralFunction = useCallback(() => {
    clearTimeout(functionTimeoutHandler.current);

    clearTimeout(maxWaitHandler.current);

    maxWaitHandler.current = undefined;

    maxWaitArgs.current = [];

    functionTimeoutHandler.current = undefined;

    leadingCall.current = false;
  }, []);

  useEffect(
    () => () => {
      isComponentUnmounted.current = true;
    },
    []
  );

  const debouncedCallback: GeneralFunction = useCallback(
    (...args) => {
      maxWaitArgs.current = args;

      clearTimeout(functionTimeoutHandler.current);

      if (leadingCall.current) {
        leadingCall.current = false;
      }

      if (!functionTimeoutHandler.current && leading && !leadingCall.current) {
        debouncedFunction.current(...args);

        leadingCall.current = true;
      }

      functionTimeoutHandler.current = window.setTimeout(() => {
        let shouldCallFunction = true;

        if (leading && leadingCall.current) {
          shouldCallFunction = false;
        }

        cancelDebouncedCallback();

        if (!isComponentUnmounted.current && trailing && shouldCallFunction) {
          debouncedFunction.current(...args);
        }
      }, delay);

      if (maxWait && !maxWaitHandler.current && trailing) {
        maxWaitHandler.current = window.setTimeout(() => {
          const callbackArgs = maxWaitArgs.current;

          cancelDebouncedCallback();

          if (!isComponentUnmounted.current) {
            debouncedFunction.current.apply(null, callbackArgs);
          }
        }, maxWait);
      }
    },
    [maxWait, delay, cancelDebouncedCallback, leading, trailing]
  );

  const callPending: GeneralFunction = useCallback(() => {
    if (!functionTimeoutHandler.current) {
      return;
    }

    debouncedFunction.current.apply(null, maxWaitArgs.current);

    cancelDebouncedCallback();
  }, [cancelDebouncedCallback]);

  return [debouncedCallback as typeof callback, cancelDebouncedCallback, callPending];
};

export default useDebouncedCallback;
